//
// arc-catalog.ts — what is on the menu
//
// The single list of what the arc menu contains: five first-tier buttons,
// four second-tier buttons under each of the four genres, and the map
// furniture that lives in Settings instead.
//
// **Nothing else in the app is allowed to hold a second copy of this.** The
// structure it replaced had one switch statement in the renderer, another in
// the dial catalogue and a third in the settings sheet, and they drifted:
// precipitation and temperature had render logic and no control at all for
// months because only two of the three lists knew about them.
//
// Mirrors `Views/Arc/ArcCatalog.swift`. Build 1.5, item 7.
//

import type { ContactKey, FurnitureKey, GenreId, LayerKey } from "../types";

export interface SeatEntry {
  key: ContactKey;
  /** Shown on hover on the web, and in the HUD readout on every tap. */
  label: string;
  /** Icon file under `public/icons/`. */
  icon: string;
}

export interface GenreEntry {
  id: GenreId;
  /** The three-letter tag the header shows while this genre has anything on. */
  tag: string;
  label: string;
  icon: string;
  seats: SeatEntry[];
}

/**
 * The four genres, in the order they sit on the arc: left to right, SEA,
 * AIR, SAT, WET, then settings. Dave's order from the brainstorm.
 */
export const GENRES: GenreEntry[] = [
  {
    id: "sea",
    tag: "SEA",
    label: "watercraft",
    icon: "sea.svg",
    seats: [
      { key: "seaCargo", label: "Cargo & Container", icon: "sea-cargo.svg" },
      { key: "seaTanker", label: "Tankers", icon: "sea-tanker.svg" },
      { key: "seaPassenger", label: "Passenger", icon: "sea-passenger.svg" },
      { key: "seaFishing", label: "Fishing & Working", icon: "sea-fishing.svg" },
    ],
  },
  {
    id: "air",
    tag: "AIR",
    label: "aircraft",
    icon: "air.svg",
    seats: [
      { key: "airCommercial", label: "Commercial", icon: "air-commercial.svg" },
      { key: "airPrivate", label: "Private / GA", icon: "air-private.svg" },
      { key: "airMilitary", label: "Military", icon: "air-military.svg" },
      { key: "airRotorcraft", label: "Rotorcraft", icon: "air-rotorcraft.svg" },
    ],
  },
  {
    id: "sat",
    tag: "SAT",
    label: "satellites",
    icon: "sat.svg",
    seats: [
      { key: "satStarlink", label: "Starlink", icon: "sat-starlink.svg" },
      { key: "satStations", label: "Stations", icon: "sat-stations.svg" },
      { key: "satNavWeather", label: "Navigation & Weather", icon: "sat-nav.svg" },
      { key: "satLuminaries", label: "Luminaries & NEO", icon: "sat-luminaries.svg" },
    ],
  },
  {
    id: "wet",
    tag: "WET",
    label: "weather",
    icon: "wet.svg",
    seats: [
      { key: "wetWind", label: "Wind", icon: "wet-wind.svg" },
      { key: "wetPrecipitation", label: "Precipitation", icon: "wet-precip.svg" },
      { key: "wetTemperature", label: "Temperature", icon: "wet-temp.svg" },
      { key: "wetClouds", label: "Clouds", icon: "wet-clouds.svg" },
    ],
  },
];

/** Settings is the fifth seat and is not a genre — it opens a HUD list. */
export const SETTINGS_SEAT = { id: "settings", label: "settings", icon: "settings.svg" } as const;

/**
 * Map furniture — grid, parallels, place pins and orbital trails. Not
 * contacts, so they get no genre button; they are set once in Settings
 * rather than flicked on and off while watching. Build 1.5, item 12.
 */
export const FURNITURE: Array<{ key: FurnitureKey; label: string }> = [
  { key: "graticule", label: "Grid & Labels" },
  { key: "parallels", label: "Parallels" },
  { key: "places", label: "Place Pins" },
  { key: "orbitalTrails", label: "Orbital Trails" },
];

const LABELS: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const g of GENRES) for (const s of g.seats) m[s.key] = s.label;
  for (const f of FURNITURE) m[f.key] = f.label;
  return m;
})();

export function labelFor(key: LayerKey): string {
  return LABELS[key] ?? key;
}

export function genreOf(key: ContactKey): GenreEntry | undefined {
  return GENRES.find((g) => g.seats.some((s) => s.key === key));
}

/** Every contact key belonging to a genre — used for "is this genre lit?". */
export function keysOfGenre(id: GenreId): ContactKey[] {
  return GENRES.find((g) => g.id === id)?.seats.map((s) => s.key) ?? [];
}
